import { z } from 'zod';
import { uuidSchema } from './common.schema.js';

/**
 * User-Management Schemas — SSOT
 *
 * Zod schemas for the user-management domain used by both the
 * backend API contract and the Vue UI layer.
 */

// ── Roles ────────────────────────────────────────────────────────────────────

export const UserRoleCodeSchema = z.enum([
  'TECHNICAL_ADMIN',
  'CLINICAL_ADMIN',
  'NICU_MANAGER',
  'NURSE',
  'PREP_TECHNICIAN',
  // ── Control-plane roles ───────────────────────────────────────────────────
  'ADMIN',
  'PRODUCTION_SUPPORT',
  'PRODUCT_TEAM',
]);
export type UserRoleCode = z.infer<typeof UserRoleCodeSchema>;

/**
 * @deprecated Derive i18n-aware labels in the UI layer via
 * `createEnumTranslation` from `@tms-phoenix/ui-vue`.
 * Kept for backward compatibility in components that haven't migrated yet.
 */
export const USER_ROLE_OPTIONS: { value: UserRoleCode; label: string }[] = [
  { value: 'TECHNICAL_ADMIN', label: 'Technical Admin' },
  { value: 'CLINICAL_ADMIN', label: 'Clinical Admin' },
  { value: 'NICU_MANAGER', label: 'NICU Manager' },
  { value: 'NURSE', label: 'Nurse' },
  { value: 'PREP_TECHNICIAN', label: 'Prep Technician' },
];

/** Value-only codes for role enum — labels should come from i18n via `useEnumTranslation`. */
export const USER_ROLE_CODES: readonly UserRoleCode[] =
  UserRoleCodeSchema.options;

// ── User statuses ────────────────────────────────────────────────────────────

export const UserStatusSchema = z.enum([
  'CONFIRMED',
  'FORCE_CHANGE_PASSWORD',
  'RESET_REQUIRED',
  'EXTERNAL_PROVIDER',
  'UNCONFIRMED',
  'DISABLED',
  'UNKNOWN',
]);
export type UserStatus = z.infer<typeof UserStatusSchema>;

export const CognitoStatusSchema = z.enum([
  'CONFIRMED',
  'FORCE_CHANGE_PASSWORD',
  'RESET_REQUIRED',
  'EXTERNAL_PROVIDER',
  'UNCONFIRMED',
  'DISABLED',
]);
export type CognitoStatus = z.infer<typeof CognitoStatusSchema>;

// ── Legacy status values (tms-api-service) ───────────────────────────────────
// The old SERVICE endpoints return ACTIVE / PENDING / DISABLED. These are NOT
// valid Cognito statuses, so we define a distinct schema and a mapper.

export const LegacyUserStatusSchema = z.enum([
  'ACTIVE',
  'PENDING',
  'DISABLED',
]);
export type LegacyUserStatus = z.infer<typeof LegacyUserStatusSchema>;

const LEGACY_STATUS_MAP: Record<LegacyUserStatus, UserStatus> = {
  ACTIVE: 'CONFIRMED',
  PENDING: 'UNCONFIRMED',
  DISABLED: 'DISABLED',
};

/** Convert a legacy SERVICE-prefix status string to the canonical UserStatus. */
export function mapLegacyStatus(raw: string): UserStatus {
  return LEGACY_STATUS_MAP[raw as LegacyUserStatus] ?? 'UNKNOWN';
}

export const IdpSourceSchema = z.string();
export type IdpSource = z.infer<typeof IdpSourceSchema>;

// ── User model (listing row) ─────────────────────────────────────────────────

export const UserSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  firstName: z.string(),
  lastName: z.string(),
  roleCodes: z.array(UserRoleCodeSchema).min(1),
  roleStatus: UserStatusSchema,
  cognitoStatus: CognitoStatusSchema,
  /** Access status — separate from Cognito roleStatus; controls whether the user can log in. */
  enabled: z.boolean().optional(),
  idpSource: IdpSourceSchema,
  jobTitle: z.string().optional(),
  department: z.string().optional(),
  employeeNumber: z.string().optional(),
  phoneNumber: z.string().optional(),
  locationId: uuidSchema.optional(),
  careUnitId: uuidSchema.optional(),
  jitCreated: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type User = z.infer<typeof UserSchema>;

// ── Legacy User model (SERVICE-prefix endpoints) ─────────────────────────────
// The old tms-api-service returns roleStatus as ACTIVE/PENDING/DISABLED and has
// no cognitoStatus field. Use `toLegacyUser` when you need to normalise a
// legacy response into the canonical User shape.

export const LegacyUserSchema = UserSchema.omit({ roleStatus: true, cognitoStatus: true }).extend({
  roleStatus: z.string(),
  cognitoStatus: z.string().optional(),
});
export type LegacyUser = z.infer<typeof LegacyUserSchema>;

/** Normalise a legacy SERVICE-prefix user object into the canonical User shape. */
export function normaliseLegacyUser(raw: LegacyUser): User {
  const roleStatus = mapLegacyStatus(raw.roleStatus);
  return {
    ...raw,
    roleStatus,
    cognitoStatus: (raw.cognitoStatus as CognitoStatus) || 'CONFIRMED',
  } as User;
}

// ── Add User request ─────────────────────────────────────────────────────────

export const AddUserFormValuesSchema = z.object({
  email: z.string().email('Invalid email address'),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  roleCodes: z.array(UserRoleCodeSchema).min(1),
  jobTitle: z.string().optional(),
  department: z.string().optional(),
  employeeNumber: z.string().optional(),
  phoneNumber: z.string().optional(),
  locationId: uuidSchema.optional(),
  careUnitId: uuidSchema.optional(),
});
export type AddUserFormValues = z.infer<typeof AddUserFormValuesSchema>;

// ── CSV Import types ─────────────────────────────────────────────────────────

export const CsvRowStatusSchema = z.enum([
  'VALID',
  'DUPLICATE_EMAIL',
  'INVALID_ROLE',
  'MISSING_FIELD',
]);
export type CsvRowStatus = z.infer<typeof CsvRowStatusSchema>;

export const CsvImportRowSchema = z.object({
  rowNumber: z.number(),
  email: z.string(),
  givenName: z.string(),
  familyName: z.string(),
  roleCode: z.string().optional(),
  jobTitle: z.string().optional(),
  department: z.string().optional(),
  employeeNumber: z.string().optional(),
  status: CsvRowStatusSchema,
  error: z.string().optional(),
  selected: z.boolean(),
});
export type CsvImportRow = z.infer<typeof CsvImportRowSchema>;

export const CsvImportResultSchema = z.object({
  succeeded: z.number(),
  failed: z.number(),
  errors: z.array(
    z.object({
      email: z.string(),
      reason: z.string(),
    }),
  ),
});
export type CsvImportResult = z.infer<typeof CsvImportResultSchema>;

// ── CSV File Upload types (Onboarding service) ──────────────────────────────

export const UserCsvUploadRowResultSchema = z.object({
  row: z.number(),
  email: z.string(),
  status: z.enum(['CREATED', 'FAILED']),
  sub: z.string().optional(),
  message: z.string().optional(),
});
export type UserCsvUploadRowResult = z.infer<typeof UserCsvUploadRowResultSchema>;

export const UserCsvUploadSummarySchema = z.object({
  total: z.number(),
  created: z.number(),
  failed: z.number(),
});
export type UserCsvUploadSummary = z.infer<typeof UserCsvUploadSummarySchema>;

export const UserCsvUploadResultSchema = z.object({
  results: z.array(UserCsvUploadRowResultSchema),
  summary: UserCsvUploadSummarySchema,
});
export type UserCsvUploadResult = z.infer<typeof UserCsvUploadResultSchema>;

// ── List response ────────────────────────────────────────────────────────────

export const UsersListResponseSchema = z.object({
  items: z.array(UserSchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
});
export type UsersListResponse = z.infer<typeof UsersListResponseSchema>;

// ── List query params ────────────────────────────────────────────────────────

export const ListUsersParamsSchema = z.object({
  page: z.number().optional(),
  pageSize: z.number().optional(),
  search: z.string().optional(),
  roleFilters: z.array(UserRoleCodeSchema).optional(),
  statusFilters: z.array(UserStatusSchema).optional(),
});
export type ListUsersParams = z.infer<typeof ListUsersParamsSchema>;
